/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import clsx from 'clsx';
import React, { useState } from 'react';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import { ICON_DRAG_INDICATOR } from '../../Icons';
import { SdsIcon } from '../SdsIcon';
import { Tooltip } from '../Tooltip/Tooltip';
import { SORTABLE_LIST_STRINGS, SortableListStyle } from './SortableList.constants';
import { SortableListProps } from './SortableList.props';

export const SortableList = <T extends Object>({
  classes,
  renderContent,
  items,
  onChange,
  presentationStyle = SortableListStyle.Default,
  disabled = false,
  ...rest
}: SortableListProps<T>): JSX.Element => {
  const [isDragging, setIsDragging] = useState(false);

  const { t } = useTranslation();
  const labels = {
    dragHandle: t(SORTABLE_LIST_STRINGS.dragHandle),
    dragToChangeOrder: t(SORTABLE_LIST_STRINGS.dragToChangeOrder),
    pressSpaceToLift: t(SORTABLE_LIST_STRINGS.pressSpaceToLift),
    reorder: t(SORTABLE_LIST_STRINGS.reorder),
  };

  const onDragStart = (): void => {
    setIsDragging(true);
  };
  const onDragEnd = (result: DropResult): void => {
    setIsDragging(false);
    const { source, destination } = result;
    if (!destination) {
      return;
    }

    const nextItems = Array.from(items);
    const [reorderedItem] = nextItems.splice(source.index, 1);
    nextItems.splice(destination.index, 0, reorderedItem);
    onChange && onChange(nextItems);
  };

  if (disabled) {
    return (
      <div
        className={clsx(
          `sds-sortable-list presentation-only sds-sortable-list--presentation-${presentationStyle}`,
          classes,
        )}
      >
        <ul className="sds-sortable-list__list">
          {items.map((item, index) => (
            <li className={'sds-sortable-list__item'} key={item.id}>
              <div className="sds-sortable-list__item-content">{renderContent(item, index)}</div>
            </li>
          ))}
        </ul>
      </div>
    );
  }
  return (
    <div className={clsx(`sds-sortable-list sds-sortable-list--presentation-${presentationStyle}`, classes)} {...rest}>
      <DragDropContext onDragEnd={onDragEnd} onDragStart={onDragStart}>
        <Droppable droppableId="droppable">
          {(provided) => (
            <ul
              {...provided.droppableProps}
              className={clsx('sds-sortable-list__list', { 'sds-sortable-list__list--dragging': isDragging })}
              key={provided.droppableProps['data-rbd-droppable-id']}
              ref={provided.innerRef}
            >
              {items.map((item, index) => (
                <Draggable draggableId={item.id} index={index} key={item.id}>
                  {(provided, snapshot) => (
                    <>
                      <li
                        {...provided.draggableProps}
                        className={clsx('sds-sortable-list__item', {
                          'sds-sortable-list__item--dragging': snapshot.isDragging,
                        })}
                        ref={provided.innerRef}
                      >
                        <Tooltip label={labels.reorder}>
                          <div
                            {...provided.dragHandleProps}
                            aria-label={labels.dragHandle}
                            aria-roledescription={labels.pressSpaceToLift}
                            className={clsx('sds-sortable-list__item-drag-btn', {
                              'sds-sortable-list__item-drag-btn--disabled': items.length === 1,
                            })}
                            role="button"
                            tabIndex={0}
                          >
                            <SdsIcon svg={ICON_DRAG_INDICATOR} />
                          </div>
                        </Tooltip>
                        <div className="sds-sortable-list__item-content">{renderContent(item, index)}</div>
                      </li>
                    </>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </ul>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
};
